import Layout from "@/components/Layout";

const Terms = () => {
  return (
    <Layout
      seoTitle="Terms of Service - SKECHA"
      seoDescription="Read SKECHA's terms of service, AI-generated content disclaimer, usage license, and user responsibilities. Understand our service limitations and your rights."
      seoKeywords="terms of service, user agreement, AI content disclaimer, service terms, SKECHA legal"
      seoUrl="https://skecha.com/terms"
      breadcrumbs={[
        { name: "Home", url: "https://skecha.com" },
        { name: "Terms", url: "https://skecha.com/terms" },
      ]}
    >
      
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 bg-gradient-hero bg-clip-text text-transparent">
            Terms of Service
          </h1>
          
          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-lg text-muted-foreground mb-8">
              Last updated: December 2024
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                By accessing and using SKECHA ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. AI-Generated Content Disclaimer</h2>
              <p className="mb-4">
                SKECHA uses artificial intelligence technology to generate coloring pages and related content. You acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>AI-generated content may contain errors, inaccuracies, or unexpected results</li>
                <li>The Service cannot guarantee the appropriateness, accuracy, or quality of generated content</li>
                <li>Generated content may not always meet your specific requirements or expectations</li>
                <li>The AI system is continuously learning and improving, but perfection is not guaranteed</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Limitation of Liability</h2>
              <p className="mb-4">
                <strong>IMPORTANT:</strong> The content generated by SKECHA is intended for recreational and educational purposes only. You expressly acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>The Service should NOT be used in commercial settings where errors could result in financial loss</li>
                <li>The Service should NOT be used in professional or business contexts where accuracy is critical</li>
                <li>The Service should NOT be used for any purpose where mistakes could cause personal, emotional, or commercial damages</li>
                <li>SKECHA and its operators shall NOT be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of the Service</li>
                <li>You use the Service entirely at your own risk</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Use License</h2>
              <p className="mb-4">
                Permission is granted to temporarily download and use the generated coloring pages for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display (commercial or non-commercial)</li>
                <li>Attempt to decompile or reverse engineer any software contained in the Service</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. User Responsibilities</h2>
              <p className="mb-4">
                As a user of SKECHA, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Use the Service only for lawful purposes</li>
                <li>Not attempt to generate inappropriate, harmful, or offensive content</li>
                <li>Review all generated content before use, especially when intended for children</li>
                <li>Not overload or attempt to disrupt the Service</li>
                <li>Respect the intellectual property rights of others</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Service Availability</h2>
              <p className="mb-4">
                SKECHA strives to maintain service availability but does not guarantee uninterrupted access. The Service may be temporarily unavailable for maintenance, updates, or due to factors beyond our control. We reserve the right to modify, suspend, or discontinue the Service at any time without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Payment and Credits</h2>
              <p className="mb-4">
                Certain features of the Service may require payment or consumption of credits. All payments are non-refundable unless otherwise stated. Credits do not expire but may be subject to terms of service changes. Pricing and credit costs may change with notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Privacy</h2>
              <p className="mb-4">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Disclaimer</h2>
              <p className="mb-4">
                The materials on SKECHA are provided on an 'as is' basis. SKECHA makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Accuracy of Materials</h2>
              <p className="mb-4">
                Due to the AI-generated nature of our content, SKECHA does not warrant that any of the materials on its Service are accurate, complete, or current. SKECHA may make changes to the materials contained on its Service at any time without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Modifications</h2>
              <p className="mb-4">
                SKECHA may revise these terms of service for its Service at any time without notice. By using this Service, you are agreeing to be bound by the then current version of these terms of service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Governing Law</h2>
              <p className="mb-4">
                These terms and conditions are governed by and construed in accordance with the laws and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Contact Information</h2>
              <p className="mb-4">
                If you have any questions about these Terms of Service, please contact us through our support channels.
              </p>
            </section>

            <div className="bg-muted p-6 rounded-lg mt-8">
              <p className="text-sm text-muted-foreground">
                <strong>Remember:</strong> SKECHA is an AI-powered creative tool designed for fun and educational purposes. Always review generated content and use your best judgment. For commercial or critical applications, please consult with appropriate professionals.
              </p>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default Terms;